import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { useConsent } from "../context/ConsentContext";

export default function CookieConsent() {
  const {
    consent,
    bannerOpen,
    prefsOpen,
    setPrefsOpen,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useConsent();

  const [analytics, setAnalytics] = useState(false);
  const [external, setExternal] = useState(false);

  useEffect(() => {
    if (prefsOpen) {
      setAnalytics(consent?.analytics ?? false);
      setExternal(consent?.external ?? false);
    }
  }, [prefsOpen, consent]);

  return (
    <>
      {bannerOpen && !prefsOpen && (
        <div
          role="region"
          aria-label="Consentement aux cookies"
          className="fixed bottom-0 inset-x-0 z-[60] px-4 pb-4 pt-2"
        >
          <div className="mx-auto max-w-3xl rounded-2xl border border-[#D8CCB9] bg-[#F4F1EA] p-5 md:p-6 shadow-[0_20px_60px_-20px_rgba(21,21,21,0.45)]">
            <h2 className="font-serif-display text-lg md:text-xl font-semibold text-[#25372D]">
              Votre vie privée compte
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#3d3a33]">
              Nous utilisons des cookies nécessaires au fonctionnement du site
              et, avec votre accord, certains cookies supplémentaires pour
              améliorer votre expérience et mesurer l'audience. Vous pouvez
              accepter, refuser ou personnaliser vos choix.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
              <Button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="flex-1 h-11 rounded-full bg-[#25372D] text-white hover:bg-[#1b2a21]"
              >
                Tout accepter
              </Button>
              <Button
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="flex-1 h-11 rounded-full bg-[#25372D] text-white hover:bg-[#1b2a21]"
              >
                Tout refuser
              </Button>
              <Button
                data-testid="cookie-customize"
                onClick={() => setPrefsOpen(true)}
                variant="outline"
                className="flex-1 h-11 rounded-full border-[#25372D] text-[#25372D] hover:bg-[#25372D] hover:text-white"
              >
                Personnaliser
              </Button>
            </div>
            <a
              href="/politique-cookies"
              className="mt-3 inline-block text-xs text-[#806850] underline underline-offset-2 hover:text-[#25372D]"
            >
              En savoir plus sur les cookies
            </a>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent
          data-testid="cookie-prefs-dialog"
          className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#F4F1EA]"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#25372D]">
              Préférences de cookies
            </DialogTitle>
            <DialogDescription className="text-[#3d3a33]">
              Choisissez les cookies que vous souhaitez autoriser. Vous pouvez
              modifier vos choix à tout moment.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3.5 text-sm text-[#3d3a33]">
            <div className="rounded-xl border border-[#D8CCB9] bg-white/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-[#25372D]">
                  Cookies nécessaires
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide text-[#806850]">
                  Toujours actifs
                </span>
              </div>
              <p className="mt-1.5 leading-relaxed">
                Indispensables au bon fonctionnement du site (mémorisation de
                vos choix de cookies). Ils ne peuvent pas être désactivés.
              </p>
            </div>

            <div className="rounded-xl border border-[#D8CCB9] bg-white/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="sw-analytics"
                  className="font-medium text-[#25372D]"
                >
                  Mesure d'audience
                </label>
                <Switch
                  id="sw-analytics"
                  data-testid="cookie-switch-analytics"
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  className="data-[state=checked]:bg-[#25372D]"
                />
              </div>
              <p className="mt-1.5 leading-relaxed">
                Statistiques de fréquentation via PostHog, pour comprendre
                l'utilisation du site et l'améliorer. Désactivé par défaut.
              </p>
            </div>

            <div className="rounded-xl border border-[#D8CCB9] bg-white/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="sw-external"
                  className="font-medium text-[#25372D]"
                >
                  Contenus externes
                </label>
                <Switch
                  id="sw-external"
                  data-testid="cookie-switch-external"
                  checked={external}
                  onCheckedChange={setExternal}
                  className="data-[state=checked]:bg-[#25372D]"
                />
              </div>
              <p className="mt-1.5 leading-relaxed">
                Lecture des vidéos YouTube intégrées. YouTube peut déposer des
                cookies lors de la lecture. Désactivé par défaut.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <Button
              data-testid="cookie-save-prefs"
              onClick={() => savePreferences({ analytics, external })}
              className="flex-1 h-11 rounded-full bg-[#25372D] text-white hover:bg-[#1b2a21]"
            >
              Enregistrer mes choix
            </Button>
            <Button
              data-testid="cookie-reject-all-prefs"
              onClick={rejectAll}
              variant="outline"
              className="flex-1 h-11 rounded-full border-[#25372D] text-[#25372D] hover:bg-[#25372D] hover:text-white"
            >
              Tout refuser
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
